import { CSSResultGroup, TemplateResult, html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, state } from 'lit/decorators.js';
import comboxStyles from '../sic-combo-box/sic-combo-box.css.lit';
import styledTextStyles from '../sic-styled-text/sic-styled-text.css.lit';
import '../sic-combo-box/sic-combo-box';

export type JsonContactItem = {
  name: string;
  contact: Array<string>;
};
export type JsonContactTopic = {
  name: string;
  topics: Array<JsonContactItem>;
};
export type JsonContactAPIResult = {
  contact_wizard_terms: Array<JsonContactTopic>;
};

/**
 * The SicContactUs component
 * @element sic-contact-us
 *
 * Lightdom styles in sic-contact-us.lightdom.css included in /shared.css
 */
@customElement('sic-contact-us')
export class SicContactUs extends OutlineElement {
  static styles: CSSResultGroup = [styledTextStyles, comboxStyles];
  /**
   * Exposes full component to light-DOM
   */
  protected createRenderRoot() {
    return this;
  }
  @property({ type: String })
  heading: string;

  @property({ type: String, attribute: 'heading-level' })
  headingLevel = '3';

  @property({ type: String, attribute: 'label-1' })
  label1 = '--Select an Option--';

  @property({ type: String, attribute: 'label-2' })
  label2 = '--Select an Option--';

  @property({ type: String, attribute: 'title-1' })
  title1: string;

  @property({ type: String, attribute: 'title-2' })
  title2: string;

  @property({ type: String, attribute: 'data-url' })
  dataURL: string;

  @property({ type: String })
  loaded = 'loading';

  @state()
  data: { [key: string]: Array<JsonContactItem> } = {};

  @state()
  keyFromFirstList: string;

  @state()
  keyFromSecondList: string;

  @state()
  fistListData: Object;

  @state()
  secondListData: Object;

  uuid: string = self.crypto.randomUUID
    ? self.crypto.randomUUID()
    : self.crypto.getRandomValues(new Uint32Array(3)).join('-');

  urlQueryFirst = 'cu1';
  urlQuerySecond = 'cu2';

  connectedCallback() {
    super.connectedCallback();
    this.fetchJSONData();
  }

  firstUpdated() {
    this.setKeysFromUrl();
    this.getListNameData();
  }

  render(): TemplateResult {
    const secondDisabled =
      this.keyFromFirstList === undefined ? 'true' : 'false';
    return html`
      <section class="sic-contact-us">
        <sic-heading level-size="${this.headingLevel}">
          <h2>${this.heading}</h2>
        </sic-heading>
        <div class="sic-contact-us--content">
          <div class="sic-contact-us--dropdown sic-contact-us--dropdown-left">
            <sic-combo-box
              combo-box-lable=${this.title1}
              label=${this.label1}
              .listData=${this.fistListData}
              active-key=${this.keyFromFirstList}
              @comboActiveKey=${this.setFirstKey}
            >
            </sic-combo-box>
          </div>
          <div class="sic-contact-us--dropdown sic-contact-us--dropdown-right">
            <sic-combo-box
              disable="${secondDisabled}"
              combo-box-lable=${this.title2}
              label=${this.label2}
              .listData=${this.secondListData}
              active-key=${this.keyFromSecondList}
              @comboActiveKey=${this.setSecondKey}
            >
            </sic-combo-box>
          </div>
        </div>
        <div class="sic-contact-us--contacts" aria-live="polite">
          ${this.keyFromSecondList
            ? html`<sic-heading level-size="2">
                <h2>Here are your choices:</h2>
              </sic-heading>`
            : null}
          ${this.keyFromSecondList ? this.getContactMarkup() : null}
        </div>
      </section>
    `;
  }

  setFirstKey(e: CustomEvent) {
    const { key } = e.detail;
    this.keyFromFirstList = key;
    this.keyFromSecondList = '';
    this.setUrlQueryParameter(this.urlQueryFirst, key, this.urlQuerySecond);
    this.getListNameData(this.keyFromFirstList);
  }

  setSecondKey(e: CustomEvent) {
    const { key } = e.detail;
    this.keyFromSecondList = key;
    this.setUrlQueryParameter(this.urlQuerySecond, key);
  }

  processStateData(response: JsonContactAPIResult) {
    const responseData = response['contact_wizard_terms'];
    Object.entries(responseData).map(
      entry => (this.data[`${entry[1].name}`] = [...entry[1].topics])
    );
    this.loaded = 'true';
    // Get data first dropdown list
    this.getListNameData();

    // Get data second dropdown list if first list key is set.
    if (this.keyFromFirstList) {
      this.getListNameData(this.keyFromFirstList);
    }
  }

  /**
   * Fetches JSON data from URL, and sets this.listItems with processed data.
   * @param url
   */
  async fetchJSONData() {
    if (!this.dataURL) {
      this.dataURL = `${window.location.origin}/json/contact-wizard`;
    }
    const fetchResponse = await this.handleFetchRequest(this.dataURL);
    this.processStateData(fetchResponse);
  }

  /**
   *
   * @param key
   * @returns If 'key' parameter is passed, will return the value of that key from this.data.
   * If no 'key' is passed will return the top level keys of this.data used to populate the first dropdown menu.
   */
  getListNameData(key?: string) {
    const names: { [key: string]: string } = {};
    if (this.loaded === 'failed') {
      return;
    }

    if (this.loaded === 'loading') {
      setTimeout(() => this.getListNameData(key), 0);
    }

    if (this.loaded === 'true') {
      if (key) {
        this.data[key].forEach(entry => (names[entry.name] = entry.name));
        this.secondListData = names;

        if (!this.secondListData.hasOwnProperty(this.keyFromSecondList)) {
          this.keyFromSecondList = '';
        }
      }
      if (!key) {
        Object.keys(this.data).forEach(entry => (names[entry] = entry));
        this.fistListData = names;
      }
    }
  }

  /**
   * Fetches data from url.
   * @param url
   * @returns json parsed response or error.
   */
  async handleFetchRequest(url: string) {
    try {
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      this.loaded = 'failed';
      throw new Error('no data');
    }
  }

  getContactMarkup() {
    if (!this.data[this.keyFromFirstList]) {
      return;
    }
    const content = this.data[this.keyFromFirstList].filter(
      entry => entry.name === this.keyFromSecondList
    );
    if (!content.length) {
      //bail
      return;
    } else {
      const contacts: string[] = content[0].contact;
      return contacts.map(contact => unsafeHTML(contact));
    }
  }

  /**
   * Set URL query parameter based using key / value.
   * @param query - query parameter name
   * @param value - query parameter value
   * @param remove - optional query parameter name to remove from the URL
   */
  setUrlQueryParameter(query: string, value: string, remove?: string) {
    const url = new URL(location.href);
    url.searchParams.set(query, encodeURIComponent(value));
    if (remove) url.searchParams.delete(remove);
    history.pushState({}, '', url);
  }

  /**
   * Get query parameters from url and set setFirstKey and setSecondKey
   */
  setKeysFromUrl() {
    const params = new URLSearchParams(document.location.search);
    const first = params.get(this.urlQueryFirst);
    const second = params.get(this.urlQuerySecond);

    if (!first && !second) {
      return;
    }
    this.keyFromFirstList = first ? decodeURIComponent(first) : '';
    this.keyFromSecondList = second ? decodeURIComponent(second) : '';
  }
}
